import React, { useState } from "react";
import {
  useGetMe,
  useGetMyOnboarding,
  useGetMyMsa,
  getGetMyOnboardingQueryKey,
  getGetMyMsaQueryKey,
} from "@workspace/api-client-react";
import { useClerk, useUser } from "@clerk/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  LogOut,
  ShieldCheck,
  FileCheck2,
  Landmark,
  FileSignature,
  FileText,
  Clock,
} from "lucide-react";
import { BankChangeFlowDialog } from "@/components/BankChangeFlowDialog";
import { MsaContractDocument } from "@/components/onboarding/MsaContractDocument";
import {
  ONBOARDING_DOCUMENT_KEYS,
  DOCUMENT_LABELS,
  DOCUMENT_PATH_FIELDS,
  rejectionReasonLabel,
} from "@/lib/ficaReview";

const ROLE_LABELS: Record<string, string> = {
  agent: "Agent",
  master_dealer: "Master Dealer",
  account_manager: "Account Manager",
  admin: "Administrator",
  finance: "Finance",
  poc_support: "PoC Support",
  operations: "Operations",
};

// Reveal only the last 4 digits of a bank account number. Masking happens
// client-side for display; the full value is never logged.
function maskAccountNumber(value: string | null | undefined): string {
  if (!value) return "—";
  const digits = value.replace(/\s/g, "");
  if (digits.length <= 4) return "••••";
  return `•••• ${digits.slice(-4)}`;
}

export function Settings() {
  const { data: me, isLoading, error } = useGetMe();
  const { user } = useUser();
  const { signOut } = useClerk();
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [msaOpen, setMsaOpen] = useState(false);

  const isAgent = me?.role === "agent";
  const { data: onboarding } = useGetMyOnboarding({
    query: {
      enabled: isAgent,
      queryKey: getGetMyOnboardingQueryKey(),
    },
  });
  // The MSA applies to field-sales agents only; `msa.required` is false for
  // every other agent type, so the card below stays hidden for them.
  const { data: msa } = useGetMyMsa({
    query: { enabled: isAgent, queryKey: getGetMyMsaQueryKey() },
  });

  if (isLoading)
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  if (error || !me)
    return <div className="p-6 text-red-500">Failed to load your profile.</div>;

  const reviewByKey = new Map(
    (onboarding?.documentReviews ?? []).map((r) => [r.documentKey, r]),
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0B132B]">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your profile and account.
        </p>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">My profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16 border border-gray-200">
              <AvatarImage src={me.avatarUrl || user?.imageUrl} />
              <AvatarFallback className="bg-[#0B132B] text-white text-lg">
                {me.firstName?.charAt(0) || me.fullName.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-lg font-semibold text-[#0B132B]">
                {me.fullName}
              </p>
              <p className="text-sm text-gray-500">{me.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name" value={me.fullName} />
            <Field label="Email" value={me.email} />
            {isAgent && (
              <Field label="Phone" value={onboarding?.phone || "—"} />
            )}
            <Field label="Role" value={ROLE_LABELS[me.role] || me.role} />
            {me.dealerCode && (
              <Field label="Dealer code" value={me.dealerCode} />
            )}
            {isAgent && (
              <Field
                label="Agent ID"
                value={me.agentId != null ? String(me.agentId) : "Not linked"}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {isAgent && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-[#0B132B]" />
              <CardTitle className="text-lg text-[#0B132B]">
                FICA documents
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-gray-50">
              {ONBOARDING_DOCUMENT_KEYS.map((key) => {
                const uploaded =
                  (onboarding?.[DOCUMENT_PATH_FIELDS[key]] ?? null) != null;
                const review = reviewByKey.get(key);
                const status = review?.status ?? null;
                return (
                  <div key={key} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-sm text-gray-600">
                        {DOCUMENT_LABELS[key]}
                      </span>
                      <FicaBadge uploaded={uploaded} status={status} />
                    </div>
                    {status === "rejected" ? (
                      <div className="mt-1.5 rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                        <p className="text-xs font-medium text-red-800">
                          {rejectionReasonLabel(review?.rejectionReason)}
                        </p>
                        {review?.comment ? (
                          <p className="text-xs text-red-700 mt-0.5 whitespace-pre-line">
                            {review.comment}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {isAgent && msa?.required && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-[#0B132B]" />
              <CardTitle className="text-lg text-[#0B132B]">
                Field Sales Agent Agreement
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4">
              {msa.fullySigned ? (
                <ShieldCheck className="w-5 h-5 text-[#00A859] mt-0.5 shrink-0" />
              ) : (
                <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              )}
              <p className="text-sm text-gray-500">
                {msa.fullySigned
                  ? "Your agreement is fully signed and counter-signed by Mooya."
                  : msa.agentSigned
                    ? "You have signed your agreement. It is awaiting Mooya's counter-signature."
                    : "You have not signed your agreement yet."}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => setMsaOpen(true)}
                className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
              >
                View agreement
              </Button>
              {msa.documentAvailable ? (
                <a
                  href="/api/me/msa/document"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-[#0B132B] transition-colors hover:border-[#E6007E] hover:text-[#E6007E]"
                >
                  <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                  Download PDF
                </a>
              ) : null}
            </div>
          </CardContent>
        </Card>
      )}

      {isAgent && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-[#0B132B]" />
              <CardTitle className="text-lg text-[#0B132B]">
                Banking details
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Bank" value={onboarding?.bankName || "—"} />
              <Field
                label="Account number"
                value={maskAccountNumber(onboarding?.accountNumber)}
              />
              <Field
                label="Account type"
                value={onboarding?.accountType || "—"}
              />
              <Field label="Branch code" value={onboarding?.branchCode || "—"} />
            </div>
            <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4">
              <ShieldCheck className="w-5 h-5 text-[#00A859] mt-0.5 shrink-0" />
              <p className="text-sm text-gray-500">
                Updating your banking details requires verification with a
                one-time PIN sent to your phone by SMS and to your email.
              </p>
            </div>
            <Button
              onClick={() => setBankDialogOpen(true)}
              className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
            >
              Update banking details
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4">
            <ShieldCheck className="w-5 h-5 text-[#00A859] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-[#0B132B]">
                Sign-in is managed securely
              </p>
              <p className="text-sm text-gray-500">
                Update your password, email, and connected accounts from the
                account portal accessible via your avatar.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              signOut({ redirectUrl: import.meta.env.BASE_URL || "/" })
            }
            className="border-gray-300 text-[#0B132B] hover:bg-gray-50"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </CardContent>
      </Card>

      {isAgent && (
        <BankChangeFlowDialog
          open={bankDialogOpen}
          onOpenChange={setBankDialogOpen}
        />
      )}

      {isAgent && msa?.required && (
        <Dialog open={msaOpen} onOpenChange={setMsaOpen}>
          <DialogContent className="max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Field Sales Agent Agreement</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] overflow-y-auto">
              <MsaContractDocument
                contract={msa.contract}
                prefill={msa.prefill}
                agentSignedName={msa.agentSignedName}
                agentSignedAt={msa.agentSignedAt}
                agentSignatureImage={msa.agentSignatureImage}
                adminSignedName={msa.adminSignedName}
                adminSignedAt={msa.adminSignedAt}
                adminSignatureImage={msa.adminSignatureImage}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function FicaBadge({
  uploaded,
  status,
}: {
  uploaded: boolean;
  status: "pending" | "accepted" | "rejected" | null;
}) {
  const { label, cls } = !uploaded
    ? { label: "Not uploaded", cls: "bg-gray-100 text-gray-500" }
    : status === "accepted"
      ? { label: "Verified", cls: "bg-[#00A859]/10 text-[#00A859]" }
      : status === "rejected"
        ? { label: "Rejected", cls: "bg-red-50 text-red-700" }
        : { label: "Pending review", cls: "bg-amber-50 text-amber-700" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <p className="text-sm font-medium text-[#0B132B]">{value}</p>
    </div>
  );
}
